import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getLeaderboard, getMyPoints } from '../services/api';

const MEDALS = ['🥇', '🥈', '🥉'];

export default function LeaderboardWidget({ currentMemberId }) {
  const [top5, setTop5] = useState([]);
  const [myPoints, setMyPoints] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const fetchedRef = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;
    loadData();
  }, []);

  const loadData = async () => {
    setError(null);
    try {
      const [lbRes, ptsRes] = await Promise.all([
        getLeaderboard('all', 5),
        getMyPoints().catch(() => null),
      ]);
      if (lbRes?.leaderboard) setTop5(lbRes.leaderboard);
      if (ptsRes) setMyPoints(ptsRes);
    } catch (err) {
      setError(err.message || 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="card-paper p-4">
        <div className="animate-pulse h-24 rounded-xl" style={{ background: 'var(--bg-input)' }} />
      </div>
    );
  }

  if (error) {
    return (
      <div className="card-paper p-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-extrabold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <span className="text-lg">🏆</span>
            <span className="text-gradient-amber">Top voices</span>
          </h3>
        </div>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{error}</p>
        <button
          onClick={() => { fetchedRef.current = false; setLoading(true); loadData(); }}
          className="mt-2 text-xs font-bold transition-colors"
          style={{ color: 'var(--accent)' }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (top5.length === 0 && !myPoints?.total) return null;

  return (
    <div className="card-paper p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-extrabold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <span className="text-lg">🏆</span>
          <span className="text-gradient-amber">Top voices</span>
        </h3>
        <Link
          to="/leaderboard"
          className="text-xs font-bold transition-colors"
          style={{ color: 'var(--accent)' }}
          onMouseEnter={e => e.currentTarget.style.opacity = '0.7'}
          onMouseLeave={e => e.currentTarget.style.opacity = '1'}
        >
          Leaderboard →
        </Link>
      </div>

      {/* My Points */}
      {myPoints && myPoints.total > 0 && (
        <div
          className="rounded-xl p-3 mb-4"
          style={{
            background: 'linear-gradient(135deg, var(--accent-glow), var(--color-info-dim))',
            border: '1px solid var(--border-accent)',
          }}
        >
          <div className="flex items-center justify-between">
            <span className="text-sm font-bold" style={{ color: 'var(--text-secondary)' }}>Your Points</span>
            <span className="text-xl font-black text-gradient-amber tabular-nums">{myPoints.total}</span>
          </div>
          <div className="flex gap-3 mt-1 text-xs font-bold" style={{ color: 'var(--text-faint)' }}>
            <span>{myPoints.breakdown?.attendance || 0} check-ins</span>
            <span>{myPoints.breakdown?.speech || 0} speeches</span>
          </div>
        </div>
      )}

      {/* Top 5 */}
      {top5.length > 0 && (
        <div className="space-y-1.5">
          {top5.map((entry, i) => {
            const isMe = entry.member_id === currentMemberId;
            return (
              <div
                key={entry.member_id}
                className="flex items-center justify-between px-3 py-2 rounded-xl text-sm transition-all"
                style={isMe
                  ? { background: 'var(--accent-glow)', border: '1px solid var(--border-accent)' }
                  : { background: 'transparent' }
                }
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm w-6 text-center">
                    {i < 3 ? MEDALS[i] : <span className="text-xs font-extrabold" style={{ color: 'var(--text-faint)' }}>{i + 1}</span>}
                  </span>
                  <div
                    className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-extrabold"
                    style={isMe
                      ? { background: 'var(--accent-dim)', color: 'var(--accent)' }
                      : { background: 'var(--color-info-dim)', color: 'var(--color-info)' }
                    }
                  >
                    {entry.member_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                  <span
                    className="font-bold truncate max-w-[120px] text-sm"
                    style={{ color: isMe ? 'var(--accent)' : 'var(--text-primary)' }}
                  >
                    {entry.member_name}
                  </span>
                </div>
                <span
                  className="font-extrabold tabular-nums"
                  style={{ color: isMe ? 'var(--accent)' : 'var(--text-secondary)' }}
                >
                  {entry.total}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
